import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import QuestionnaireV2RefPage from '@/app/components/questionnaireV2/components/questionnaireV2Ref.page';
import { useGetQuestionnaire } from '@/app/services/questionnaireV2.service';
import useVisitorSession from '@/hooks/useVisitorSession';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { QuizletVersionConfig } from './quizlet-config';
import { ROUTE_PAYMENT } from '@/constants/routes';

interface QuizletUnifiedWrapperProps {
  config: QuizletVersionConfig;
}

export default function QuizletUnifiedWrapper({ config }: QuizletUnifiedWrapperProps) {
  const navigate = useNavigate();
  const setIntercomIsVisible = useSetIntercomIsVisible();
  const { initializeSession } = useVisitorSession();
  const [isSessionReady, setIsSessionReady] = useState(false);

  // Only fetch questionnaire after session is initialized
  const { data: questionnaire, isLoading, isError } = useGetQuestionnaire(config.uuid, isSessionReady);
  // Hide intercom
  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  // Initialize visitor session and mark as ready when complete
  useEffect(() => {
    initializeSession().then((session) => {
      if (session) {
        setIsSessionReady(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle completion - navigate to version-specific results
  const handleComplete = () => {
    navigate(ROUTE_PAYMENT);
  };

  // Show loading while initializing session or loading questionnaire
  if (!isSessionReady || (isLoading && !questionnaire)) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center bg-black">
        <div className="pt-4 text-lg text-white mb-2">
          {!isSessionReady ? 'Initializing session...' : 'Loading questionnaire...'}
        </div>
      </div>
    );
  }

  // Show error state if fetch failed
  if (isError && !questionnaire) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black">
        <div className="text-lg text-white">Failed to load questionnaire. Please refresh the page.</div>
      </div>
    );
  }

  // If we're still loading but have no questionnaire, keep showing loading
  // This prevents unmounting during retries
  if (!questionnaire) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center bg-black">
        <div className="pt-4 text-lg text-white mb-2">Loading questionnaire...</div>
      </div>
    );
  }

  // Use the existing QuestionnaireV2RefPage with onComplete callback
  return <QuestionnaireV2RefPage questionnaire={questionnaire} onComplete={handleComplete} />;
}
